import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, Minus, Search, Users, Undo2, X } from 'lucide-react';
import { horaireCourt } from '../horaire';
import { useFenetreModale } from '../useFenetreModale';
import { aPrevenir, texteExperience } from './classementRemplacants';
import { jourCourt, jourLong } from './dates';
import { FOCUS_ATTR, PASTILLE_ATTR, pastilleId } from './constantes';
import { ListeRemplacants } from './ListeRemplacants';
import { couleurDuPole } from './PastilleSeance';
import { phrase } from './selection';
import {
  auLieuDe, estLie, instantane, liees, lireChoix, memesAtelier, statut, type EtatSeance, type Instantane, type Seance,
} from './seancesDemande';
import { aidePrevenir, CHOIX_SEANCE, initiales, LIBELLE_ETAT, phraseNotificationDesignation } from './textesDemande';
import { useAncrage } from './useAncrage';
import type { Nd } from './useNouvelleDemande';

const ICONE = { rech: Search, trouve: Check, off: Minus } as const;
const MAX_PUCES = 4;
/** Plusieurs phrases à la suite, chacune avec un seul point final (les dates abrégées en portent déjà un : « … 10 déc. »). */
const phrases = (...p: string[]): string => p.filter(Boolean).map(phrase).join(' ');

interface Note { qui: number; snaps: Instantane[] }
interface Local { mode: 'trouve' | null; changer: boolean; filtre: string; actif: number; note: Note | null; toutesPuces: boolean }

interface Props {
  nd: Nd;
  seance: Seance;
  /** 'c' : ouverte depuis le calendrier ; 'p' : depuis le panneau. */
  ou: 'c' | 'p';
  /** Ouverte pour choisir le/la garant·e du cadre qui manque : le focus va droit sur la liste à remplir. */
  versGarant?: boolean;
  onFermer: () => void;
}

/**
 * La fenêtre d'une séance, ancrée à côté d'elle. En haut, « Pour cette séance » : les trois choix, chacun avec ce qu'il fait. Dessous, ce qui va avec le
 * choix : « À rechercher » → « Prévenir aussi personnellement » ; « Remplaçant·e trouvé·e » → la personne choisie avec « Retirer » à côté de son nom et la
 * case « Aussi pour… » juste dessous (ou la liste, pour la choisir) ; « Pas de remplaçant·e à trouver » → rien de plus.
 */
export function FenetreSeance({ nd, seance: s, ou, versGarant, onFermer }: Props) {
  const { dispatch, choixEtat, personnesParId, seancesChoisies, dire, apresRendu, reglages, etat } = nd;
  const cle = s.cle;
  const c = lireChoix(choixEtat, cle);
  const p = c.remp != null ? personnesParId[c.remp] ?? null : null;
  const [local, setLocal] = useState<Local>({ mode: null, changer: false, filtre: '', actif: -1, note: null, toutesPuces: false });
  const sortie = useRef(false);   // fermée par un clic ailleurs : le focus reste où la personne l'a mis
  const etatSeance: EtatSeance = statut(s, choixEtat);
  const mode: 'rech' | 'trouve' | 'off' = etatSeance === 'trouve' || (local.mode === 'trouve' && etatSeance !== 'off') ? 'trouve' : (etatSeance === 'off' ? 'off' : 'rech');

  const { ref } = useFenetreModale(onFermer, {
    focusInitial: () => (versGarant ? ref.current?.querySelector<HTMLElement>('#nd-garant') : null) ?? ref.current?.querySelector<HTMLElement>('input[name="nd-mode"]:checked') ?? null,
    sansRetourFocus: () => sortie.current,
  });
  const pos = useAncrage(`[${PASTILLE_ATTR}="${pastilleId(ou, cle)}"]`, ref);

  // Un clic ailleurs ferme la fenêtre (sans voler le focus) ; un clic sur une pastille la remplace par celle de l'autre séance. Phase de capture : le clic
  // qui ferme la fenêtre ne fait rien d'autre (il ne choisit ni ne retire le jour du calendrier sur lequel il tombe).
  useEffect(() => {
    const dehors = (e: PointerEvent) => {
      const t = e.target as HTMLElement;
      if (ref.current?.contains(t) || t.closest('.nd-pastille') || t.closest('.modal-overlay')) return;
      sortie.current = true;
      e.stopPropagation();
      onFermer();
    };
    document.addEventListener('pointerdown', dehors, true);
    return () => document.removeEventListener('pointerdown', dehors, true);
  }, [ref, onFermer]);

  const focaliser = (fid: string) => apresRendu(() => ref.current?.querySelector<HTMLElement>(`[${FOCUS_ATTR}="${fid}"]`)?.focus());
  const nom = (id: number) => personnesParId[id]?.display_name ?? '—';
  const autresAtelier = memesAtelier(seancesChoisies, s);

  // ── Gestes ─────────────────────────────────────────────────────────────────────────────────────────
  const restentPrevus = (qui: number) => autresAtelier.filter((o) => lireChoix(choixEtat, o.cle).remp === qui);

  /** Retirer la personne de CETTE séance (vers « À rechercher » ou « Pas de remplaçant·e à trouver ») ; « Annuler » la remet. */
  function retirer(versOff: boolean) {
    if (c.remp == null) return;
    const qui = c.remp;
    const snap = instantane(choixEtat, cle);
    dispatch({ type: 'retirerRemplacant', cle, versOff });
    setLocal((l) => ({ ...l, mode: null, changer: false, note: { qui, snaps: [snap] } }));
    const restent = restentPrevus(qui);
    dispatch({ type: 'message', texte: phrases(
      `${nom(qui)} retiré·e du ${jourCourt(s.date)}`,
      versOff ? 'Cette séance ne sera pas envoyée' : 'La séance est de nouveau « À rechercher »',
      restent.length ? `${personnesParId[qui]?.prenom} reste choisi·e ${restent.map((o) => `le ${jourCourt(o.date)}`).join(' et ')}` : '',
    ) });
    focaliser(`mode${versOff ? 'off' : 'rech'}`);
  }
  function annulerRetrait() {
    if (!local.note) return;
    dispatch({ type: 'restaurer', snaps: local.note.snaps });
    dispatch({ type: 'message', texte: `Retrait annulé : ${nom(local.note.qui)} est de nouveau choisi·e.` });
    setLocal((l) => ({ ...l, note: null, mode: null }));
    focaliser('retirer');
  }
  function retirerAussi() {
    if (!local.note) return;
    const rs = restentPrevus(local.note.qui);
    dispatch({ type: 'retirerAussi', cles: rs.map((o) => o.cle) });
    dispatch({ type: 'message', texte: phrase(`${nom(local.note.qui)} retiré·e aussi ${rs.map((o) => `du ${jourCourt(o.date)}`).join(' et ')}`) });
    setLocal((l) => ({ ...l, note: l.note && { ...l.note, snaps: [...l.note.snaps, ...rs.map((o) => instantane(choixEtat, o.cle))] } }));
    focaliser('annuler');
  }
  function changerMode(v: 'rech' | 'trouve' | 'off') {
    if (c.remp != null && v !== 'trouve') { retirer(v === 'off'); return; }
    dispatch({ type: 'off', cle, off: v === 'off' });
    setLocal((l) => ({ ...l, note: null, changer: false, mode: v === 'trouve' ? 'trouve' : null }));
    dispatch({ type: 'message', texte: v === 'trouve' ? dire('Choisissez dans la liste la personne qui vous remplace.', 'Choisissez dans la liste la personne qui remplace {P}.')
      : v === 'off' ? `${s.atelierNom}, ${jourCourt(s.date)} : cette séance ne sera pas envoyée.` : `${s.atelierNom}, ${jourCourt(s.date)} : « À rechercher ».` });
    focaliser(`mode${v}`);
  }
  /** Choisir quelqu'un dans la liste : la fenêtre reste ouverte et montre la personne, avec « Retirer » et la case « Aussi pour… ». */
  function choisir(id: number) {
    const q = personnesParId[id];
    setLocal((l) => ({ ...l, mode: null, changer: false, filtre: '', actif: -1, note: null }));
    if (c.remp === id || !q) { focaliser('retirer'); return; }
    const aussi = autresAtelier.filter((o) => !choixEtat.off[o.cle]);
    dispatch({ type: 'designer', cle, id });
    dispatch({ type: 'message', texte: phrases(
      `${q.display_name} est choisi·e ${c.remp != null ? `à la place de ${nom(c.remp)} ` : ''}pour ${s.atelierNom}, ${jourCourt(s.date)}`,
      aussi.length ? `Cochez « Aussi pour… » sous son nom pour ${aussi.length > 1 ? 'les autres séances' : "l'autre séance"} de cet atelier` : '',
    ) });
    focaliser(q.is_stagiaire ? 'garant' : aussi.length ? 'aussi' : 'retirer');
  }

  // ── Contenu selon le choix ─────────────────────────────────────────────────────────────────────────
  const cands = useMemo(() => nd.candidatsDe(s), [nd.candidatsDe, s]);   // eslint-disable-line react-hooks/exhaustive-deps
  const puces = reglages.scopeCollegues === 'off' ? [] : aPrevenir(cands, etat.absentId);
  const pucesVisibles = local.toutesPuces ? puces : puces.slice(0, MAX_PUCES);
  const lieesA = liees(seancesChoisies, choixEtat, s);
  const cochee = estLie(seancesChoisies, choixEtat, s);
  const candidatP = p ? cands.find((x) => x.personne.id === p.id) : undefined;

  const restentApresRetrait = local.note ? restentPrevus(local.note.qui) : [];

  return (
    <div
      ref={ref} role="dialog" aria-label={`Séance ${s.atelierNom}, ${jourLong(s.date)}`} className="nd-fenetre"
      style={pos ? { left: pos.left, top: pos.top } : { left: 0, top: 0, opacity: 0 }}   /* jamais « visibility: hidden » : le focus ne peut pas entrer dans un élément caché */
    >
      <div className="nd-fenetre-tete">
        <div>
          <div className="nd-fenetre-t"><span className="nd-dot" style={{ ['--pole' as string]: couleurDuPole(nd.meta, s.poleId) ?? 'var(--ink-3)' }} aria-hidden="true" />{s.atelierNom}</div>
          <div className="text-xs text-ink-3">{[jourCourt(s.date), horaireCourt(s.debut, s.fin), s.lieu].filter(Boolean).join(' · ')}</div>
        </div>
        <button type="button" className="close-btn" aria-label="Fermer" onClick={onFermer}><X size={16} aria-hidden="true" /></button>
      </div>

      {/* 1. Les trois choix, chacun avec ce qu'il fait */}
      <fieldset className="nd-modes">
        <legend className="nd-micro">Pour cette séance</legend>
        {CHOIX_SEANCE.map((m) => {
          const I = ICONE[m.etat as 'rech' | 'trouve' | 'off'];
          const on = mode === m.etat;
          return (
            <label key={m.etat} className={`nd-mode nd-st-${m.etat}${on ? ' is-on' : ''}`}>
              <input type="radio" name="nd-mode" value={m.etat} checked={on} {...{ [FOCUS_ATTR]: `mode${m.etat}` }} onChange={() => changerMode(m.etat as 'rech' | 'trouve' | 'off')} />
              <span className="nd-mode-txt">
                <span className="nd-mode-t"><I size={14} aria-hidden="true" />{LIBELLE_ETAT[m.etat]}</span>
                <span className="nd-mode-d">{dire(m.vous, m.tiers)}</span>
              </span>
            </label>
          );
        })}
      </fieldset>

      {/* Juste après « Retirer » : ce qui s'est passé, « Annuler », et la même personne restée sur une autre séance (« Retirer aussi ») */}
      {local.note && (
        <div className="nd-note" role="group" aria-label="Retrait">
          <Undo2 size={14} aria-hidden="true" />
          <div>
            <p>
              {nom(local.note.qui)} retiré·e {local.note.snaps.length > 1 ? `de ${local.note.snaps.length} séances` : 'de cette séance'}.{' '}
              <button type="button" className="nd-lien nd-lien--ligne" {...{ [FOCUS_ATTR]: 'annuler' }} onClick={annulerRetrait}>Annuler</button>
            </p>
            {restentApresRetrait.length > 0 && (
              <p>
                {phrase(`${personnesParId[local.note.qui]?.prenom} reste choisi·e ${restentApresRetrait.map((o) => `le ${jourCourt(o.date)}`).join(' et ')}`)}{' '}
                <button type="button" className="nd-lien nd-lien--ligne" {...{ [FOCUS_ATTR]: 'retirerAussi' }} onClick={retirerAussi}>Retirer aussi</button>
              </p>
            )}
          </div>
        </div>
      )}

      {/* 2. Ce qui va avec le choix */}
      {mode === 'rech' && puces.length > 0 && (
        <div className="nd-prevenir">
          <div className="nd-micro" id="nd-prev-t">Prévenir aussi personnellement (facultatif)</div>
          <p className="nd-aide" id="nd-prev-aide">{aidePrevenir(nd.voix)}</p>
          <div className="nd-puces" role="group" aria-labelledby="nd-prev-t" aria-describedby="nd-prev-aide">
            {pucesVisibles.map((x) => {
              const on = c.prevenir.includes(x.personne.id);
              return (
                <button key={x.personne.id} type="button" className={`chip${on ? ' active' : ''}`} aria-pressed={on} {...{ [FOCUS_ATTR]: `prev${x.personne.id}` }}
                  title={texteExperience(x)} onClick={() => dispatch({ type: 'prevenir', cle, id: x.personne.id })}>
                  {on ? <Check size={12} aria-hidden="true" /> : <span aria-hidden="true">+</span>}{x.personne.display_name}
                </button>
              );
            })}
          </div>
          {puces.length > MAX_PUCES && (
            <button type="button" className="nd-lien" onClick={() => setLocal((l) => ({ ...l, toutesPuces: !l.toutesPuces }))}>
              {local.toutesPuces ? 'Voir moins' : `Voir les autres (${puces.length - MAX_PUCES})`}
            </button>
          )}
        </div>
      )}

      {mode === 'trouve' && p && !local.changer && (
        <>
          <div className="nd-choisi">
            <span className="nd-av" aria-hidden="true">{initiales(p)}</span>
            <span className="nd-choisi-txt">
              <span className="nd-nom-choisi">{p.display_name}{p.is_stagiaire && <span className="badge">Stagiaire</span>}</span>
              {candidatP && (
                <>
                  <span className="nd-opt-xp">{texteExperience(candidatP)}</span>
                  <span className={`nd-opt-dispo${candidatP.dispo ? '' : ' is-non'}`}>
                    {candidatP.dispo ? <Check size={12} aria-hidden="true" /> : <AlertTriangle size={12} aria-hidden="true" />}{candidatP.dispo ? (candidatP.verifiee ? 'disponible' : 'disponibilité non vérifiée') : candidatP.raison}
                  </span>
                </>
              )}
            </span>
            <button type="button" className="btn btn-sm nd-retirer" {...{ [FOCUS_ATTR]: 'retirer' }} aria-label={`Retirer ${p.display_name} de cette séance`} onClick={() => retirer(false)}>
              <X size={13} aria-hidden="true" />Retirer
            </button>
            {lieesA.length > 0 && (
              <label className="nd-case nd-aussi">
                <input type="checkbox" checked={cochee} {...{ [FOCUS_ATTR]: 'aussi' }} onChange={(e) => {
                  const oui = e.target.checked;
                  const dates = lieesA.map((o) => jourCourt(o.date)).join(', ');
                  const remplaces = lieesA.map((o) => auLieuDe(choixEtat, s, o)).filter((x): x is number => x != null && x !== c.remp).map(nom);
                  dispatch({ type: 'lier', cle, oui });
                  dispatch({ type: 'message', texte: oui ? phrase(`${p.display_name} aussi choisi·e : ${dates}${remplaces.length ? ` (à la place de ${remplaces.join(', ')})` : ''}`) : `${dates} : retour au choix d'avant.` });
                }} />
                <span>
                  {lieesA.length > 1 ? `Aussi pour les ${lieesA.length} autres séances de cet atelier` : "Aussi pour l'autre séance de cet atelier"}{' : '}
                  <strong>{lieesA.map((o) => { const a = auLieuDe(choixEtat, s, o); return `${jourCourt(o.date)}${a != null && a !== c.remp ? ` (au lieu de ${nom(a)})` : ''}`; }).join(', ')}</strong>
                </span>
              </label>
            )}
          </div>
          {p.is_stagiaire && (
            <div className="nd-garant">
              <label className="form-label" htmlFor="nd-garant">Garant·e du cadre <span className="nd-requis">obligatoire</span></label>
              <select id="nd-garant" className="form-select" {...{ [FOCUS_ATTR]: 'garant' }} value={c.garant ?? ''} onChange={(e) => dispatch({ type: 'garant', cle, id: Number(e.target.value) || null })}>
                <option value="">Choisir le/la garant·e…</option>
                {nd.personnes.filter((q) => !q.is_externe && !q.is_stagiaire && q.id !== etat.absentId && q.id !== p.id).sort((a, b) => a.display_name.localeCompare(b.display_name, 'fr'))
                  .map((q) => <option key={q.id} value={q.id}>{q.display_name}</option>)}
              </select>
              <p className="nd-aide">{p.prenom} est stagiaire : un·e garant·e du cadre est obligatoire.</p>
            </div>
          )}
          <p className="nd-aide">
            {phraseNotificationDesignation(p)} {dire('En cas de refus, vous serez prévenu·e.', `En cas de refus, ${nd.voix.prenom || 'la personne absente'} et vous serez prévenu·e·s.`)}
          </p>
          <button type="button" className="nd-lien" {...{ [FOCUS_ATTR]: 'changer' }} onClick={() => { setLocal((l) => ({ ...l, changer: true, note: null })); focaliser('cb'); }}>
            <Users size={14} aria-hidden="true" />Choisir quelqu'un d'autre
          </button>
        </>
      )}

      {mode === 'trouve' && (!p || local.changer) && (
        <>
          <ListeRemplacants
            candidats={cands} choisi={c.remp} filtre={local.filtre} actif={local.actif}
            onFiltre={(v) => setLocal((l) => ({ ...l, filtre: v }))} onActif={(i) => setLocal((l) => ({ ...l, actif: i }))} onChoisir={choisir}
            etiquette={p ? `Choisir quelqu'un d'autre que ${p.display_name} :` : dire('Qui vous remplace ?', 'Qui remplace {P} ?')}
          />
          {p
            ? <button type="button" className="nd-lien" {...{ [FOCUS_ATTR]: 'garder' }} onClick={() => { setLocal((l) => ({ ...l, changer: false, filtre: '' })); focaliser('retirer'); }}><Undo2 size={14} aria-hidden="true" />Garder {p.display_name}</button>
            : <p className="nd-aide">Tant que personne n'est choisi·e, la séance reste « À rechercher ».</p>}
        </>
      )}

      {mode === 'off' && (
        <p className="nd-aide">
          {dire(
            "Si votre absence est dans Tipee, l'équipe gestion en sera quand même informée et décidera.",
            "Si l'absence est dans Tipee, l'équipe gestion en sera quand même informée et décidera.",
          )}
        </p>
      )}

      <div className="nd-fenetre-pied">
        <span />
        <button type="button" className="btn btn-sm btn-accent" {...{ [FOCUS_ATTR]: 'terminer' }} onClick={onFermer}>Terminé</button>
      </div>
    </div>
  );
}
